/**
 * Vehicle Report Service
 * Handles vehicle report API calls and data transformations
 */

import { makeAuthenticatedRequest, createApiError, getErrorMessage } from './apiService';
import { API_ENDPOINTS } from '../config/api';
import type { NvsStolenSummary } from './nvsService';

// TypeScript interfaces matching backend DTOs

export interface VehicleReportRequest {
  vin: string;
}

export interface VehicleIdentification {
  identificationID: string;
}

export interface HistoricTitleRecord {
  vehicleIdentification?: VehicleIdentification;
  titleIssuingAuthorityName?: string;
  titleIssueDate?: string;
  vehicleOdometerReadingMeasure?: number;
  vehicleOdometerReadingUnitCode?: string;
}

export interface TitleRecord {
  vehicleIdentification?: VehicleIdentification;
  titleIssuingAuthorityName?: string;
  titleIssueDate?: string;
  vehicleOdometerReadingMeasure?: number;
  vehicleOdometerReadingUnitCode?: string;
  recordMatchSequenceID?: string;
  historicTitleAbstract?: HistoricTitleRecord[];
}

export interface ReportingEntity {
  reportingEntityCategoryCode?: string;
  identificationID?: string;
  reportingEntityCategoryText?: string;
  entityName?: string;
  locationCityName?: string;
  locationStateUSPostalServiceCode?: string;
  telephoneNumberFullID?: string;
  contactEmailID?: string;
}

export interface VehicleBrand {
  reportingEntityAbstract?: ReportingEntity;
  vehicleBrandCode?: string;
  vehicleBrandDescription?: string;
  vehicleBrandDate?: string;
  vehicleObtainedDate?: string;
  vehicleDispositionText?: string;
  vehicleIntendedForExportCode?: string;
  // Individual record detection (set by backend)
  isIndividualRecord?: boolean;
}

export interface ExceptionElement {
  exceptionElementTagText?: string;
  exceptionElementOccurrenceID?: string;
}

export interface NmvtisException {
  exceptionID?: string;
  exceptionCode?: string;
  exceptionText?: string;
  exceptionTypeCode?: string;
  exceptionSevereIndicator?: boolean;
  exceptionCategoryCode?: string;
  exceptionSeverityLevel?: string;
  exceptionElement?: ExceptionElement;
  exceptionActorText?: string;
}

export interface ConsumerIdentification {
  identificationID?: string;
}

export interface NmvtisVehicleDataResponse {
  vehicleIdentification?: VehicleIdentification;
  title: TitleRecord[];
  vehicleDesignationAbstract: VehicleBrand[];
  exception: NmvtisException[];
  consumerIdentification?: ConsumerIdentification;
  rawSoapResponse?: string;
}

export interface VehicleReportResponse {
  reportId: string;
  vin: string;
  vehicleYear?: string;
  vehicleMake?: string;
  vehicleModel?: string;
  fetchedAt: string;
  wasCached: boolean;
  billingAmount: number;
  data?: NmvtisVehicleDataResponse;
  stolenRecords?: NvsStolenSummary[];
}

export interface VehicleReportSummary {
  id: string;
  vin: string;
  fetchedAt: string;
  lastAccessedAt: string;
  daysUntilExpiration: number;
  isFresh: boolean;
  nmvtisCost: number;
}

export interface VehicleReportListResponse {
  reports: VehicleReportSummary[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Response from the NMVTIS pricing endpoint
 */
export interface NmvtisPricingResponse {
  costPerVin: number;
  serviceType: string;
}

/**
 * Fetch NMVTIS pricing (flat rate per VIN lookup)
 * Used to display estimated cost before search
 */
export const fetchNmvtisPricing = async (): Promise<NmvtisPricingResponse> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.VEHICLE_REPORTS.PRICING,
    { method: 'GET' }
  );

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Create or retrieve a vehicle history report
 * Returns cached data if available within 24 hours for the same client/user+VIN;
 * otherwise fetches fresh data from NMVTIS
 */
export const createVehicleReport = async (
  vin: string
): Promise<VehicleReportResponse> => {
  try {
    const response = await makeAuthenticatedRequest(API_ENDPOINTS.VEHICLE_REPORTS.CREATE, {
      method: 'POST',
      body: JSON.stringify({ vin }),
    });

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    const data: VehicleReportResponse = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get a list of vehicle reports for the authenticated user/client
 */
export const getVehicleReports = async (
  pageNumber: number = 1,
  pageSize: number = 20,
  vin?: string
): Promise<VehicleReportListResponse> => {
  try {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });

    if (vin) {
      params.append('vin', vin);
    }

    const url = `${API_ENDPOINTS.VEHICLE_REPORTS.LIST}?${params.toString()}`;
    const response = await makeAuthenticatedRequest(url, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    const data: VehicleReportListResponse = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get a specific vehicle report by ID
 */
export const getVehicleReportById = async (
  reportId: string
): Promise<VehicleReportResponse> => {
  try {
    const response = await makeAuthenticatedRequest(
      API_ENDPOINTS.VEHICLE_REPORTS.GET(reportId),
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    const data: VehicleReportResponse = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Helper function to extract vehicle information from NMVTIS data
 */
export const extractVehicleInfo = (data?: NmvtisVehicleDataResponse, stolenRecords?: NvsStolenSummary[]) => {
  if (!data) return null;

  const latestTitle = data.title && data.title.length > 0 ? data.title[0] : null;
  const brands = data.vehicleDesignationAbstract || [];
  const exceptions = data.exception || [];

  // Use the VIN from the latest title record if available (may include correction suffix like 'A')
  const currentVin = latestTitle?.vehicleIdentification?.identificationID
    || data.vehicleIdentification?.identificationID
    || 'Unknown';

  // Extract all title records from the title array
  const allTitleRecords = (data.title || []).map(title => ({
    state: title.titleIssuingAuthorityName || 'Unknown',
    date: title.titleIssueDate || null,
    odometer: title.vehicleOdometerReadingMeasure || null,
    odometerUnit: title.vehicleOdometerReadingUnitCode || 'M',
    sequenceId: title.recordMatchSequenceID || '',
    vin: title.vehicleIdentification?.identificationID || null,
  }));

  // Extract historic titles from historicTitleAbstract and deduplicate
  const historicTitlesMap = new Map<string, typeof allTitleRecords[0]>();
  (data.title || []).forEach(title => {
    (title.historicTitleAbstract || []).forEach(historic => {
      // Create unique key based on state, date, odometer, and VIN
      const key = `${historic.titleIssuingAuthorityName}-${historic.titleIssueDate}-${historic.vehicleOdometerReadingMeasure}-${historic.vehicleIdentification?.identificationID}`;
      if (!historicTitlesMap.has(key)) {
        historicTitlesMap.set(key, {
          state: historic.titleIssuingAuthorityName || 'Unknown',
          date: historic.titleIssueDate || null,
          odometer: historic.vehicleOdometerReadingMeasure || null,
          odometerUnit: historic.vehicleOdometerReadingUnitCode || 'M',
          sequenceId: '',
          vin: historic.vehicleIdentification?.identificationID || null,
        });
      }
    });
  });
  const historicFromAbstract = Array.from(historicTitlesMap.values());

  // Sort all title records by date (newest first), then by sequenceId for stable ordering
  allTitleRecords.sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    const dateCompare = new Date(b.date).getTime() - new Date(a.date).getTime();
    if (dateCompare !== 0) return dateCompare;
    // If dates are equal, sort by sequenceId (lower = first reported = current)
    return (a.sequenceId || '').localeCompare(b.sequenceId || '');
  });

  // NMVTIS rules: A vehicle can only have ONE current title at a time
  // The most recent title by date is the current title; all others are historical
  // Keep all title[] entries (no deduplication) - each represents a distinct record from NMVTIS
  const titlesWithHistoricFlag: Array<typeof allTitleRecords[0] & { isHistoric: boolean }> =
    allTitleRecords.map((title, index) => ({
      ...title,
      // Only the first (most recent by date) is the current title
      isHistoric: index > 0,
    }));

  // Add historic titles from historicTitleAbstract (these are always historical)
  // Skip any that match already-added titles by state+date+odometer+vin
  const seenTitleKeys = new Set<string>();
  titlesWithHistoricFlag.forEach(t => {
    seenTitleKeys.add(`${t.state}-${t.date}-${t.odometer}-${t.vin}`);
  });

  historicFromAbstract.forEach(historic => {
    const key = `${historic.state}-${historic.date}-${historic.odometer}-${historic.vin}`;
    if (!seenTitleKeys.has(key)) {
      seenTitleKeys.add(key);
      titlesWithHistoricFlag.push({
        ...historic,
        isHistoric: true,
      });
    }
  });

  // Re-sort to ensure current title first, then historical by date (newest first)
  const currentTitles = titlesWithHistoricFlag.filter(t => !t.isHistoric);
  const historicTitles = titlesWithHistoricFlag.filter(t => t.isHistoric);

  // Sort historic titles by date (newest first)
  historicTitles.sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  // Combine: current title first, then historic titles
  const allTitles = [...currentTitles, ...historicTitles];

  return {
    vin: currentVin,
    titleState: latestTitle?.titleIssuingAuthorityName || 'Unknown',
    titleDate: latestTitle?.titleIssueDate || null,
    odometer: latestTitle?.vehicleOdometerReadingMeasure || null,
    odometerUnit: latestTitle?.vehicleOdometerReadingUnitCode || 'M',
    titles: allTitles, // All title records (current + historic)
    brands: brands.map(b => ({
      code: b.vehicleBrandCode || '',
      description: b.vehicleBrandDescription || 'Unknown',
      date: b.vehicleBrandDate || b.vehicleObtainedDate || null,
      reportingEntity: (() => {
        const entityName = (b.reportingEntityAbstract?.entityName || '').trim();
        const catCode = b.reportingEntityAbstract?.reportingEntityCategoryCode || '';
        const catText = b.reportingEntityAbstract?.reportingEntityCategoryText || '';
        const stateCode = b.reportingEntityAbstract?.locationStateUSPostalServiceCode || '';
        const idCode = (b.reportingEntityAbstract?.identificationID || '').trim();
        if (catCode === 'S') {
          const name = entityName || stateCode || idCode;
          return name ? `${name} (State)` : 'Unknown';
        }
        if (entityName && catText) return `${entityName} (${catText})`;
        return entityName || 'Unknown';
      })(),
      // Additional fields for categorization
      reportingEntityCategoryCode: b.reportingEntityAbstract?.reportingEntityCategoryCode || '',
      reportingEntityCategoryText: b.reportingEntityAbstract?.reportingEntityCategoryText || '',
      disposition: b.vehicleDispositionText || '',
      state: b.reportingEntityAbstract?.locationStateUSPostalServiceCode || '',
      // Contact and address info for Insurance and Junk/Salvage reporters
      city: b.reportingEntityAbstract?.locationCityName || '',
      phone: b.reportingEntityAbstract?.telephoneNumberFullID || '',
      email: b.reportingEntityAbstract?.contactEmailID || '',
      // Export indicator
      intendedForExport: b.vehicleIntendedForExportCode === 'Y',
      // Individual record detection
      isIndividualRecord: b.isIndividualRecord || false,
    })),
    // Reporting entity category codes: J = Junk/Salvage yards, I = Insurance companies
    // S = State, and other codes are regular title brand reporters
    // Only J and I are special "JSI" reporters - others report normal title brands
    hasTheftRecord: brands.some(b => {
      const desc = b.vehicleBrandDescription?.toLowerCase() || '';
      const brandCode = b.vehicleBrandCode || '';
      const categoryCode = b.reportingEntityAbstract?.reportingEntityCategoryCode || '';
      // JSI records (J/I) don't have title brand info, skip them for theft detection
      if (categoryCode === 'J' || categoryCode === 'I') return false;
      // NMVTIS brand code 06 is specifically for theft/stolen vehicles
      if (brandCode === '06') return true;
      // Check description but exclude "anti-theft" (which appears in "Rebuilt" brand descriptions)
      const hasTheftKeyword = desc.includes('theft') || desc.includes('stolen');
      const isAntiTheft = desc.includes('anti-theft');
      return hasTheftKeyword && !isAntiTheft;
    }) || ((stolenRecords?.filter(r => r.status?.toLowerCase() !== 'inactive').length ?? 0) > 0),
    hasSalvageRecord: brands.some(b => {
      const desc = b.vehicleBrandDescription?.toLowerCase() || '';
      const categoryCode = b.reportingEntityAbstract?.reportingEntityCategoryCode || '';
      // For Insurance records, check disposition for "SALVAGE"
      if (categoryCode === 'I') {
        const disposition = b.vehicleDispositionText?.toLowerCase() || '';
        return disposition === 'salvage';
      }
      // Junk/Salvage yards don't indicate salvage title, skip them
      if (categoryCode === 'J') return false;
      // For regular title brands, check description
      return desc.includes('salvage');
    }),
    hasFloodRecord: brands.some(b => {
      const desc = b.vehicleBrandDescription?.toLowerCase() || '';
      const categoryCode = b.reportingEntityAbstract?.reportingEntityCategoryCode || '';
      // JSI records don't have flood info
      if (categoryCode === 'J' || categoryCode === 'I') return false;
      return desc.includes('flood');
    }),
    hasJunkRecord: brands.some(b => {
      const desc = b.vehicleBrandDescription?.toLowerCase() || '';
      const categoryCode = b.reportingEntityAbstract?.reportingEntityCategoryCode || '';
      // Any record from a Junk/Salvage yard (category "J") indicates vehicle passed through salvage yard
      if (categoryCode === 'J') return true;
      // For Insurance records, check disposition for "CRUSH" or "JUNK"
      if (categoryCode === 'I') {
        const disposition = b.vehicleDispositionText?.toLowerCase() || '';
        return disposition === 'crush' || disposition === 'junk';
      }
      // For regular title brands, check description
      return desc.includes('junk') || desc.includes('dismantled');
    }),
    exceptions: exceptions.map(e => ({
      code: e.exceptionCode || 'Unknown',
      text: e.exceptionText || 'Unknown exception',
      severity: e.exceptionSeverityLevel || 'Unknown',
    })),
    // Indicates whether NMVTIS returned actual records (titles or brands)
    // When false, display "No Records Found" instead of misleading "Clean" statuses
    hasRecords: allTitles.length > 0 || brands.length > 0,
    // NVS stolen/theft records (fetched in parallel with NMVTIS)
    stolenRecords: stolenRecords || [],
  };
};

/**
 * State Contact Information Interfaces
 */
export interface StateContactInfo {
  stateName: string;
  stateCode: string;
  instructions: string;
  mailingAddress?: string;
  phone?: string;
  email?: string;
  websiteUrl?: string;
  formUrl?: string;
  recordRequestUrl?: string;
  recordRequestFee?: string;
}

/**
 * Get state contact information for multiple state codes
 */
export const getStateContactInfo = async (
  stateCodes: string[]
): Promise<Record<string, StateContactInfo>> => {
  try {
    if (!stateCodes || stateCodes.length === 0) {
      return {};
    }

    // Remove duplicates and filter out empty values
    const uniqueCodes = Array.from(new Set(stateCodes.filter(code => code && code.trim())));

    const params = new URLSearchParams({
      stateCodes: uniqueCodes.join(','),
    });

    const url = `${API_ENDPOINTS.STATE_CONTACT_INFO.GET_BY_CODES}?${params.toString()}`;
    const response = await makeAuthenticatedRequest(url, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    const data: Record<string, StateContactInfo> = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching state contact info:', error);
    return {};
  }
};

/**
 * Generate PDF Request Interface
 */
export interface GeneratePdfRequest {
  vin: string;
  reportId?: string;
  requestedBy?: string;
  referenceNote?: string;
  showBrandChecklist?: boolean;
}

/**
 * Generate a PDF report for a vehicle
 * Downloads the PDF file to the user's browser
 */
export const generatePdfReport = async (
  request: GeneratePdfRequest
): Promise<void> => {
  try {
    const response = await makeAuthenticatedRequest(
      API_ENDPOINTS.VEHICLE_REPORTS.GENERATE_PDF,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    // Get the PDF blob from the response
    const blob = await response.blob();

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    let fileName = `NMVTIS_VehicleReport_${request.vin}_${new Date().toISOString().split('T')[0]}.pdf`;

    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (fileNameMatch && fileNameMatch[1]) {
        fileName = fileNameMatch[1];
      }
    }

    // Create a temporary URL for the blob
    const url = window.URL.createObjectURL(blob);

    // Create a temporary anchor element and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch PDF report as blob URL for viewing in modal
 * Returns a blob URL that can be used in an iframe
 */
export const fetchPdfForViewing = async (
  vin: string,
  reportId?: string
): Promise<string> => {
  try {
    const response = await makeAuthenticatedRequest(
      API_ENDPOINTS.VEHICLE_REPORTS.GENERATE_PDF,
      {
        method: 'POST',
        body: JSON.stringify({ vin, reportId }),
      }
    );

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    // Get the PDF blob from the response
    const blob = await response.blob();

    // Create a blob URL for viewing
    const blobUrl = window.URL.createObjectURL(blob);
    return blobUrl;
  } catch (error) {
    throw error;
  }
};

/**
 * Generate PDF from stored activity log data Request Interface
 */
export interface GeneratePdfFromLogRequest {
  logId: string;
  requestedBy?: string;
  referenceNote?: string;
  showBrandChecklist?: boolean;
}

/**
 * Brand checklist item - represents a single brand code with detected/non-detected status
 */
export interface BrandChecklistItem {
  title: string;
  description: string;
  isDetected: boolean;
  count: number;
  displayOrder: number;
  brandDate?: string;
  isJsiCategory?: boolean;
}

/**
 * Fetch the full brand checklist for a vehicle report
 * Returns all brand codes with their detected/non-detected status
 */
export const fetchBrandChecklist = async (
  vin: string,
  reportId?: string
): Promise<BrandChecklistItem[]> => {
  const params = new URLSearchParams({ vin });
  if (reportId) params.append('reportId', reportId);

  const response = await makeAuthenticatedRequest(
    `${API_ENDPOINTS.VEHICLE_REPORTS.BRAND_CHECKLIST}?${params.toString()}`,
    { method: 'GET' }
  );

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return response.json();
};

/**
 * Fetch PDF report from stored activity log data as blob URL for viewing in modal
 * Uses the stored NMVTIS response from the activity log instead of making a new API call
 * Returns a blob URL that can be used in an iframe
 */
export const fetchPdfFromLogForViewing = async (
  logId: string
): Promise<string> => {
  try {
    const response = await makeAuthenticatedRequest(
      API_ENDPOINTS.VEHICLE_REPORTS.GENERATE_PDF_FROM_LOG,
      {
        method: 'POST',
        body: JSON.stringify({ logId }),
      }
    );

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    // Get the PDF blob from the response
    const blob = await response.blob();

    // Create a blob URL for viewing
    const blobUrl = window.URL.createObjectURL(blob);
    return blobUrl;
  } catch (error) {
    throw error;
  }
};

/**
 * Generate and download PDF report from stored activity log data
 */
export const generatePdfFromLog = async (
  request: GeneratePdfFromLogRequest
): Promise<void> => {
  try {
    const response = await makeAuthenticatedRequest(
      API_ENDPOINTS.VEHICLE_REPORTS.GENERATE_PDF_FROM_LOG,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    // Get the PDF blob from the response
    const blob = await response.blob();

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    let fileName = `NMVTIS_VehicleReport_${new Date().toISOString().split('T')[0]}.pdf`;

    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (fileNameMatch && fileNameMatch[1]) {
        fileName = fileNameMatch[1];
      }
    }

    // Create a temporary URL for the blob
    const url = window.URL.createObjectURL(blob);

    // Create a temporary anchor element and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    throw error;
  }
};

// ============================================================================
// Bulk NMVTIS Report Types and Functions
// ============================================================================

/**
 * Single VIN item with optional reference note for bulk operations
 */
export interface BulkVinItem {
  vin: string;
  referenceNote?: string;
}

/**
 * Request DTO for bulk vehicle report retrieval
 */
export interface BulkVehicleReportRequest {
  items: BulkVinItem[];
}

/**
 * Individual result within a bulk operation
 */
export interface BulkVehicleReportItem {
  index: number;
  vin: string;
  referenceNote?: string;
  success: boolean;
  errorMessage?: string;
  report?: VehicleReportResponse;
}

/**
 * Response DTO for bulk vehicle report operation
 */
export interface BulkVehicleReportResponse {
  batchId: string;
  processedAt: string;
  totalRequested: number;
  totalSucceeded: number;
  totalFailed: number;
  totalCached: number;
  totalBillable: number;
  totalBillingAmount: number;
  results: BulkVehicleReportItem[];
}

/**
 * Request DTO for generating a combined PDF from multiple VINs
 */
export interface GenerateBulkPdfRequest {
  items: BulkVinItem[];
  requestedBy?: string;
  showBrandChecklist?: boolean;
}

/**
 * Create or retrieve vehicle history reports for multiple VINs
 * Processes VINs sequentially in the order provided
 */
export const createBulkVehicleReports = async (
  items: BulkVinItem[]
): Promise<BulkVehicleReportResponse> => {
  try {
    const response = await makeAuthenticatedRequest(
      API_ENDPOINTS.VEHICLE_REPORTS.BULK_CREATE,
      {
        method: 'POST',
        body: JSON.stringify({ items }),
      }
    );

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    const data: BulkVehicleReportResponse = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Generate a combined PDF for multiple VINs with page breaks between each report
 * Downloads the PDF file to the user's browser
 */
export const generateBulkPdfReport = async (
  request: GenerateBulkPdfRequest
): Promise<void> => {
  try {
    const response = await makeAuthenticatedRequest(
      API_ENDPOINTS.VEHICLE_REPORTS.GENERATE_BULK_PDF,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    // Get the PDF blob from the response
    const blob = await response.blob();

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    let fileName = `NMVTIS_BulkReport_${new Date().toISOString().split('T')[0]}.pdf`;

    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (fileNameMatch && fileNameMatch[1]) {
        fileName = fileNameMatch[1];
      }
    }

    // Create a temporary URL for the blob
    const url = window.URL.createObjectURL(blob);

    // Create a temporary anchor element and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    throw error;
  }
};

/**
 * Parse VINs from text input (newline or comma separated)
 * Returns array of VIN strings, trimmed and uppercased
 */
export const parseVinsFromText = (text: string): string[] => {
  if (!text || !text.trim()) return [];

  // Split by newlines or commas, trim whitespace, convert to uppercase
  const vins = text
    .split(/[\n,]+/)
    .map(vin => vin.trim().toUpperCase())
    .filter(vin => vin.length >= 5 && vin.length <= 17);

  // Remove duplicates while preserving order
  return Array.from(new Set(vins));
};

/**
 * Parse VINs from CSV content
 * Requires a CSV with a VIN header column. ReferenceNote column is optional.
 * Throws an error if no VIN column is found.
 */
export const parseVinsFromCsv = (csvContent: string): BulkVinItem[] => {
  if (!csvContent || !csvContent.trim()) return [];

  const lines = csvContent.split(/\r?\n/).filter(line => line.trim());
  if (lines.length === 0) return [];

  const items: BulkVinItem[] = [];
  const seenVins = new Set<string>();

  // Check if first line looks like a header (contains text that doesn't look like a VIN)
  const firstLine = lines[0];
  const firstLineLower = firstLine.toLowerCase();
  const firstParts = firstLine.split(',').map(p => p.trim());

  // A VIN is 5-17 alphanumeric characters. If first cell looks like a VIN, treat as no header.
  const firstCellLooksLikeVin = /^[A-HJ-NPR-Z0-9]{5,17}$/i.test(firstParts[0]);

  // Check for explicit header keywords
  const hasHeaderKeywords = firstLineLower.includes('vin') || firstLineLower.includes('reference');

  // If first cell doesn't look like a VIN, we need to validate it has proper headers
  const hasHeader = hasHeaderKeywords || !firstCellLooksLikeVin;
  const startIndex = hasHeader ? 1 : 0;

  // Determine column indices
  let vinIndex = -1;
  let refNoteIndex = -1;

  if (hasHeader) {
    const headers = firstLine.split(',').map(h => h.trim().toLowerCase());
    vinIndex = headers.findIndex(h => h === 'vin' || h.includes('vin'));
    refNoteIndex = headers.findIndex(h => h === 'referencenote' || h === 'reference' || h === 'note' || h.includes('reference'));

    // If header row detected but no VIN column found, throw error
    if (vinIndex === -1) {
      throw new Error('CSV must have a "VIN" column header. Please download the template for the correct format.');
    }
  } else {
    // No header, treat first column as VIN
    vinIndex = 0;
  }

  for (let i = startIndex; i < lines.length; i++) {
    const parts = lines[i].split(',').map(p => p.trim());
    const vin = (parts[vinIndex] || '').toUpperCase().replace(/['"]/g, '');
    const refNote = refNoteIndex >= 0 ? (parts[refNoteIndex] || '').replace(/['"]/g, '') : undefined;

    // Validate VIN length
    if (vin.length >= 5 && vin.length <= 17 && !seenVins.has(vin)) {
      seenVins.add(vin);
      items.push({
        vin,
        referenceNote: refNote && refNote.trim() ? refNote.trim() : undefined,
      });
    }
  }

  return items;
};

/**
 * Generate CSV template content for bulk VIN upload
 */
export const generateCsvTemplate = (): string => {
  return 'VIN,ReferenceNote\n1HGBH41JXMN109186,Customer: John Smith\n2HGFC2F59MH123456,Invoice #12345\n';
};

/**
 * Download CSV template for bulk VIN upload
 */
export const downloadCsvTemplate = (): void => {
  const content = generateCsvTemplate();
  const blob = new Blob([content], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'NMVTIS_Bulk_Template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export { getErrorMessage };
